import React, { useState } from 'react';
import { useLocation, Outlet, useNavigate } from 'react-router-dom';
import { 
  Drawer, 
  Box, 
  Button, 
  Stack, 
  Paper, 
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Badge
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import UserMenu from './UserMenu';
import Footer from './Footer';
import VideoSubmitForm from '../feed/VideoSubmitForm';
import ArticleSubmitForm from '../feed/ArticleSubmitForm';
import PostSubmitForm from '../feed/PostSubmitForm';
import CreateEventForm from '../events/CreateEventForm';
import CreatePollDialog from '../polls/CreatePollDialog';
import FriendRequests from '../friends/FriendRequests';
import { CAUSES } from './causes';
import { useAuth } from '../../contexts/AuthContext';
import { createPoll } from '../../services/pollService';
import { Poll } from '../../types/firebase';
import { Timestamp } from '@firebase/firestore';

export default function MainLayout() {
  const location = useLocation();
  const isProfilePage = location.pathname === '/profile';
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  
  // State for modal dialogs
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showPollDialog, setShowPollDialog] = useState(false);
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [showPostForm, setShowPostForm] = useState(false);

  const handleIconClick = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#181818' }}>
      {/* Responsive App Bar Title Style */}
      {/* Custom App Bar */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '64px',
          background: '#232323',
          zIndex: 1301,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingLeft: '8px',
          paddingRight: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        }}
      >
        {/* App Icon - Left Side */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '48px',
            height: '64px',
            flexShrink: 0,
            marginLeft: '4px',
            cursor: 'pointer',
            outline: 'none',
            WebkitTapHighlightColor: 'transparent',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            msUserSelect: 'none',
          }}
          onClick={handleIconClick}
          onMouseDown={(e) => e.preventDefault()}
        >
          <Badge
            color="error"
            variant="dot"
            overlap="circular"
            invisible={!(userProfile && userProfile.friendRequests && userProfile.friendRequests.received && userProfile.friendRequests.received.length > 0)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{
              '& .MuiBadge-dot': {
                height: 12,
                minWidth: 12,
                borderRadius: '50%',
                top: 2,
                right: 2,
              }
            }}
          >
            <img
              src="/logo192.png"
              alt="Battleground State"
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                objectFit: 'cover',
              }}
            />
          </Badge>
        </div>

        {/* App Title - Centered */}
        <div
          className="bgs-app-title"
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'Times New Roman, Times, serif',
            fontWeight: 'bold',
            fontSize: 'clamp(0.75rem, 2.5vw, 1.5rem)',
            color: '#fff',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            msUserSelect: 'none',
            lineHeight: '1',
            margin: 0,
            padding: 0,
            height: '100%',
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
            marginLeft: '4px',
          }}
        >
          BATTLEGROUND STATE
        </div>

        {/* User Menu - Right Side */}
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'center',
            width: '48px',
            height: '64px',
            flexShrink: 0,
          }}
        >
          <UserMenu />
        </div>
      </div>

      {/* Left Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            width: 280,
            backgroundColor: '#232323',
            color: '#fff',
          }
        }}
      >
        {/* Drawer content */}
        <Box sx={{ p: 1 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#fff', px: 1 }}>
            Create Content
          </Typography>
          
          {currentUser ? (
            <Paper elevation={2} sx={{ backgroundColor: 'background.paper', mt: 4 }}>
              <Stack direction="column" spacing={1}>
                <Button 
                  fullWidth 
                  variant="contained" 
                  color="primary" 
                  startIcon={<AddIcon />} 
                  onClick={() => setShowVideoForm(true)}
                  sx={{ 
                    py: 1.5,
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                    fontSize: '0.875rem'
                  }}
                >
                  Share Video
                </Button>
                <Button 
                  fullWidth 
                  variant="contained" 
                  color="primary" 
                  startIcon={<AddIcon />} 
                  onClick={() => setShowArticleForm(true)}
                  sx={{ 
                    py: 1.5,
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                    fontSize: '0.875rem'
                  }}
                >
                  Share Article
                </Button>
                <Button 
                  fullWidth 
                  variant="contained" 
                  color="primary" 
                  startIcon={<AddIcon />} 
                  onClick={() => setShowPostForm(true)}
                  sx={{ 
                    py: 1.5,
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                    fontSize: '0.875rem'
                  }}
                >
                  Create Post
                </Button>
                <Button 
                  fullWidth 
                  variant="contained" 
                  color="primary" 
                  startIcon={<AddIcon />} 
                  onClick={() => setShowEventForm(true)}
                  sx={{ 
                    py: 1.5,
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                    fontSize: '0.875rem'
                  }}
                >
                  Create Event
                </Button>
                <Button 
                  fullWidth 
                  variant="contained" 
                  color="primary" 
                  startIcon={<AddIcon />} 
                  onClick={() => setShowPollDialog(true)}
                  sx={{ 
                    py: 1.5,
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                    fontSize: '0.875rem'
                  }}
                >
                  Create Poll
                </Button>
              </Stack>
            </Paper>
          ) : (
            <Paper elevation={2} sx={{ backgroundColor: 'background.paper', mt: 4, p: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
                  Sign In Required
                </Typography>
                <Typography variant="body2" sx={{ color: '#ccc', mb: 3, lineHeight: 1.5 }}>
                  To create and share content, you need to sign in with your Google account.
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary" 
                  fullWidth
                  onClick={() => {
                    handleDrawerClose();
                    navigate('/login');
                  }}
                  sx={{ 
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                    fontSize: '0.875rem'
                  }}
                >
                  Sign In to Continue
                </Button>
              </Box>
            </Paper>
          )}

          {/* Friend Requests Section */}
          {currentUser && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#fff', px: 1 }}>
                Friend Requests
              </Typography>
              <FriendRequests 
                onRequestAccepted={() => {
                  // Refresh any necessary data when a friend request is accepted
                  console.log('Friend request accepted from drawer');
                }}
              />
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Video Submit Modal */}
      <Dialog 
        open={showVideoForm} 
        onClose={() => {
          if (typeof document !== 'undefined' && document.body) {
            document.body.focus();
          }
          setShowVideoForm(false);
        }} 
        maxWidth="sm" 
        fullWidth
        disableEnforceFocus
        disableRestoreFocus
      >
        <DialogTitle>Share Video</DialogTitle>
        <DialogContent>
          <VideoSubmitForm onSubmitSuccess={() => setShowVideoForm(false)} />
        </DialogContent>
      </Dialog>

      {/* Event Create Modal */}
      <Dialog 
        open={showEventForm} 
        onClose={() => {
          if (typeof document !== 'undefined' && document.activeElement) {
            const dialog = document.querySelector('[role="dialog"]');
            if (dialog && dialog.contains(document.activeElement)) {
              (document.activeElement as HTMLElement).blur();
            }
          }
          if (typeof document !== 'undefined' && document.body) {
            document.body.focus();
          }
          setShowEventForm(false);
        }} 
        maxWidth="md" 
        fullWidth
        disableEnforceFocus
        disableRestoreFocus
      >
        <DialogTitle>Create Event</DialogTitle>
        <DialogContent>
          <CreateEventForm onSuccess={() => setShowEventForm(false)} />
        </DialogContent>
      </Dialog>

      {/* Poll Create Modal */}
      <CreatePollDialog
        open={showPollDialog}
        onClose={() => setShowPollDialog(false)}
        onSubmit={async (pollData) => {
          try {
            if (!currentUser) {
              console.error('You must be logged in to create a poll');
              return;
            }

            // Convert the poll data to the format expected by the service
            const pollToCreate: Omit<Poll, 'id' | 'createdAt' | 'updatedAt' | 'totalVotes' | 'status'> = {
              title: pollData.title,
              description: pollData.description,
              cause: pollData.cause,
              endDate: Timestamp.fromDate(pollData.endDate),
              createdBy: {
                id: currentUser.uid,
                name: currentUser.displayName || 'Anonymous',
                avatar: currentUser.photoURL || ''
              },
              options: pollData.options.map((option, index) => ({
                id: `option-${index}`,
                text: option,
                votes: 0,
                votedBy: []
              })),
              settings: {
                allowComments: true
              }
            };

            await createPoll(pollToCreate, currentUser);
            setShowPollDialog(false);
          } catch (error) {
            console.error('Error creating poll:', error);
          }
        }}
        causes={CAUSES.map(cause => ({ id: cause.id, name: cause.name }))}
      />

      {/* Article Submit Modal */}
      <Dialog 
        open={showArticleForm} 
        onClose={() => {
          if (typeof document !== 'undefined' && document.body) {
            document.body.focus();
          }
          setShowArticleForm(false);
        }} 
        maxWidth="sm" 
        fullWidth
        disableEnforceFocus
        disableRestoreFocus
      >
        <DialogTitle>Share Article</DialogTitle>
        <DialogContent>
          <ArticleSubmitForm onSubmitSuccess={() => setShowArticleForm(false)} />
        </DialogContent>
      </Dialog>

      {/* Post Submit Modal */}
      <Dialog 
        open={showPostForm} 
        onClose={() => {
          if (typeof document !== 'undefined' && document.body) {
            document.body.focus();
          }
          setShowPostForm(false);
        }} 
        maxWidth="sm" 
        fullWidth
        disableEnforceFocus
        disableRestoreFocus
      >
        <DialogTitle>Create Post</DialogTitle>
        <DialogContent>
          <PostSubmitForm onSubmitSuccess={() => setShowPostForm(false)} />
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <div
        style={{
          flexGrow: 1,
          paddingTop: '72px',
          paddingBottom: '24px',
          paddingLeft: '16px',
          paddingRight: '16px',
        }}
      >
        {isProfilePage ? <Outlet /> : <div style={{ maxWidth: 1440, margin: '0 auto' }}><Outlet /></div>}
      </div>
      <Footer />
    </div>
  );
} 